(() => {
  const formatNumber = (n) => n.toLocaleString();

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  };

  const initSection = (root) => {
    const fallbackMultiplier = parseInt(root.dataset.entriesMultiplier, 10) || 100;
    const tiers = root.querySelectorAll('[data-tier]');
    const visuals = root.querySelectorAll('.quick-entry__visual');
    const qtyInput = root.querySelector('[data-qty-input]');
    const incBtn = root.querySelector('[data-qty-increment]');
    const decBtn = root.querySelector('[data-qty-decrement]');
    const totalEl = root.querySelector('[data-entries-value]');
    const priceEl = root.querySelector('[data-active-price]');
    const descEl = root.querySelector('[data-active-description]');
    const multBadgeEl = root.querySelector('[data-active-multiplier]');
    const buyVariantInput = root.querySelector('[data-buy-variant-id]');
    const buyQtyInput = root.querySelector('[data-buy-quantity]');
    const buyForm = root.querySelector('[data-buy-form]');
    const buyButton = root.querySelector('[data-buy-button]');

    if (!tiers.length) return;

    const tierData = (el) => ({
      idx: parseInt(el.dataset.svgIndex, 10),
      priceCents: parseInt(el.dataset.priceCents, 10) || 0,
      priceMoney: el.dataset.priceMoney || '',
      variantId: el.dataset.variantId || '',
      tickets: parseInt(el.dataset.tickets, 10) || 0,
      multiplier: parseInt(el.dataset.multiplier, 10) || fallbackMultiplier,
      entries: parseInt(el.dataset.entries, 10) || 0,
      description: el.dataset.description || '',
    });
    let active = tierData(tiers[0]);

    // Per-unit giveaway entries granted for the active tier (matches the theme's
    // entry-input-properties/entry-badge calculation: multiplier * price / 100).
    const entriesPerUnit = () => active.entries || Math.round(active.multiplier * (active.priceCents / 100));

    // The cart property/attribute key (e.g. "CAPRICE GIVEAWAY"). Empty when the
    // entries feature is off, in which case we skip properties entirely.
    const giveawayName = root.dataset.giveawayName || (window.entries && window.entries.giveaway_name) || '';

    const recalc = () => {
      const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      const total = qty * active.multiplier * (active.priceCents / 100);
      if (totalEl) totalEl.textContent = formatNumber(Math.round(total));
      if (buyQtyInput) buyQtyInput.value = qty;
      if (buyVariantInput) buyVariantInput.value = active.variantId || '';
    };

    const setActive = (tierEl) => {
      active = tierData(tierEl);
      tiers.forEach((t) => t.classList.toggle('is-active', t === tierEl));
      visuals.forEach((v) => v.classList.toggle('is-active', parseInt(v.dataset.svgIndex, 10) === active.idx));
      if (priceEl) priceEl.textContent = active.priceMoney || formatMoney(active.priceCents);
      if (descEl) descEl.textContent = active.description;
      if (multBadgeEl) multBadgeEl.textContent = `${active.multiplier}X`;
      recalc();
    };

    tiers.forEach((t) => t.addEventListener('click', () => setActive(t)));

    if (incBtn) incBtn.addEventListener('click', () => {
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
      recalc();
    });
    if (decBtn) decBtn.addEventListener('click', () => {
      const v = (parseInt(qtyInput.value, 10) || 1) - 1;
      qtyInput.value = Math.max(1, v);
      recalc();
    });
    if (qtyInput) qtyInput.addEventListener('input', recalc);

    const setLoading = (isLoading) => {
      root.classList.toggle('is-loading', isLoading);
      if (buyButton) buyButton.disabled = isLoading;
    };

    // Recompute the giveaway cart attribute from every line item's property and
    // PUT it back on the cart. Mirrors snippets/entry-theme-file.liquid, which
    // normally runs on the `cart:update` event — but this flow redirects straight
    // to checkout without dispatching it, so we sync inline before leaving.
    const syncCartAttribute = async (key) => {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        let total = 0;
        for (const line of cart.items || []) {
          const raw = line.properties && line.properties[key];
          if (raw == null) continue;
          const n = parseInt(String(raw).replace(/[^\d-]/g, ''), 10);
          if (Number.isFinite(n)) total += n * (line.quantity || 1);
        }
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { [key]: String(total) } }),
        });
      } catch (err) {
        console.error('Quick Entry attribute sync failed:', err);
      }
    };

    const goToCheckout = async () => {
      if (!active.variantId) {
        console.warn('Quick Entry: active tier has no product/variant selected.');
        return;
      }
      const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      const item = { id: parseInt(active.variantId, 10), quantity: qty };
      const perUnit = entriesPerUnit();
      if (giveawayName && perUnit > 0) {
        item.properties = { [giveawayName]: String(perUnit) };
      }
      setLoading(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!res.ok) throw new Error('cart-add failed: ' + res.status);
        if (giveawayName) await syncCartAttribute(giveawayName);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('Quick Entry checkout error:', err);
        setLoading(false);
      }
    };

    if (buyButton) buyButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      goToCheckout();
    });

    // Init from the first tier
    setActive(tiers[0]);
  };

  const boot = () => document.querySelectorAll('[data-quick-entry]').forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-quick-entry]');
    if (root) initSection(root);
  });
})();
